/**
 * MessageTemplateTable コンポーネント
 *
 * メッセージテンプレートの一覧を表示するテーブルコンポーネント。
 * テーブルにはメッセージテンプレートのタイトル、カテゴリ、タイプ、作成日時、削除ボタンが含まれます。
 *
 * @file メッセージテンプレートテーブル
 * @date 2025/06/26
 */
import React from 'react';
import { Button, Modal, Spin, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { MessageTemplate } from 'src/models/messageTemplate';
import { useMessageTemplateForm } from 'src/usecases/messageTemplate/form';

/**
 * Props インターフェース
 *
 * @property messageTemplates - メッセージテンプレートの配列
 * @property usage - 使用目的（'directMessage' または 'broadcast'）
 */
interface Props {
    messageTemplates: MessageTemplate[];
    usage: 'directMessage' | 'broadcast';
}

// 配信用メッセージテンプレートの列定義
const broadcastColumns = (
    onClickDelete: (record: MessageTemplate) => void,
    uniqueCategories: string[]
): ColumnsType<MessageTemplate> => [
        {
            title: 'タイトル',
            dataIndex: 'title',
            key: 'title',
            width: 400,
            ellipsis: true,
        },
        {
            title: 'カテゴリ',
            dataIndex: 'category',
            key: 'category',
            width: 150,
            filters: uniqueCategories.map(category => ({
                text: category,
                value: category,
            })),
            onFilter: (value, record) => record.category === value,
        },
        {
            title: 'タイプ',
            dataIndex: ['broadCastMessage', 'type'],
            key: 'type',
            width: 300,
            filters: [
                { text: 'テキストのみ', value: 'text' },
                { text: '画像のみ', value: 'image' },
                { text: 'アクション付き画像のみ（クーポンなど）', value: 'template' },
                { text: 'テキスト＆画像', value: 'textAndImage' },
                { text: 'テキスト＆アクション付き画像', value: 'textAndTemplate' }
            ].map(option => ({
                text: option.text,
                value: option.value
            })),
            onFilter: (value, record) => record.broadCastMessage?.type === value,
            render: (type: string) => {
                switch (type) {
                    case 'text':
                        return 'テキストのみ';
                    case 'image':
                        return '画像のみ';
                    case 'template':
                        return 'アクション付き画像のみ（クーポンなど）';
                    case 'textAndImage':
                        return 'テキスト＆画像';
                    case 'textAndTemplate':
                        return 'テキスト＆アクション付き画像';
                    default:
                        return '不明';
                }
            }
        },
        {
            title: '作成日時',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            sorter: (a, b) => {
                const dateA = dayjs(a.createdAt);
                const dateB = dayjs(b.createdAt);
                return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
            },
            render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '操作',
            key: 'delete',
            width: 80,
            align: 'center' as const,
            render: (_, record) => (
                <Button type="link" danger onClick={(e) => {
                    e.stopPropagation();
                    const title = record.title || '';
                    const displayTitle = title.length > 50 ? title.substring(0, 50) + '...' : title;
                    Modal.confirm({
                        title: '削除の確認',
                        content: `以下のテンプレートを削除してもよろしいですか？\n\n【タイトル】\n${displayTitle}`,
                        okText: '削除',
                        okType: 'danger',
                        cancelText: 'キャンセル',
                        onOk: () => onClickDelete(record)
                    });
                }}>
                    削除
                </Button>
            ),
        },
    ];

// DM用メッセージテンプレートの列定義
const dmColumns = (
    onClickDelete: (record: MessageTemplate) => void,
    uniqueCategories: string[]
): ColumnsType<MessageTemplate> => [
        {
            title: 'メッセージテキスト',
            dataIndex: ['broadCastMessage', 'text'],
            key: 'text',
            width: 600,
            ellipsis: {
                showTitle: true,
            },
            render: (text: string) => (
                <div style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    minHeight: '42px'
                }}>
                    {text}
                </div>
            ),
        },
        {
            title: 'カテゴリ',
            dataIndex: 'category',
            key: 'category',
            width: 150,
            filters: uniqueCategories.map(category => ({
                text: category,
                value: category,
            })),
            onFilter: (value, record) => record.category === value,
        },
        {
            title: '作成日時',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            sorter: (a, b) => {
                const dateA = dayjs(a.createdAt);
                const dateB = dayjs(b.createdAt);
                return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
            },
            render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '操作',
            key: 'delete',
            width: 80,
            align: 'center' as const,
            render: (_, record) => (
                <Button type="link" danger onClick={(e) => {
                    e.stopPropagation();
                    const text = record.broadCastMessage?.text || '';
                    const displayText = text.length > 50 ? text.substring(0, 50) + '...' : text;
                    Modal.confirm({
                        title: '削除の確認',
                        content: `以下のテンプレートを削除してもよろしいですか？\n\n【メッセージ】\n${displayText}`,
                        okText: '削除',
                        okType: 'danger',
                        cancelText: 'キャンセル',
                        onOk: () => onClickDelete(record)
                    });
                }}>
                    削除
                </Button>
            ),
        },
    ];

/**
 * MessageTemplateTable コンポーネント
 *
 * メッセージテンプレートの一覧を表示するテーブルコンポーネント。
 *
 * @param messageTemplates - メッセージテンプレートの配列
 * @param usage - 使用目的（'directMessage' または 'broadcast'）
 * @returns JSX.Element
 */
export const MessageTemplateTable: React.FC<Props> = ({ messageTemplates, usage }) => {
    // ナビゲーションフック
    const navigate = useNavigate();

    // 行クリック時のハンドラ
    const onClickEdit = React.useCallback(
        (v: MessageTemplate) => {
            return {
                onClick: () => {
                    if (!v?.id) return;
                    navigate(v.id!);
                    return;
                },
            };
        },
        [navigate]
    );

    // 削除ローディング
    const [loading, isLoading] = React.useState(false);

    const _formState = useMessageTemplateForm();

    // 削除処理のハンドラ
    const handleDelete = React.useCallback(async (value: MessageTemplate) => {
        // 削除処理の開始
        isLoading(true);

        try {
            // APIを呼び出して論理削除
            await _formState.deleteMessageTemplate(value);
        } finally {
            // ローディング状態を解除
            isLoading(false);
        }
    }, []);

    // フィルタリング処理をメモ化
    const filteredTemplates = React.useMemo(() => {
        return messageTemplates.filter(
            (template) => template.usage === usage 
        ) ?? [];
    }, [messageTemplates, usage]);

    // 使用されているカテゴリを取得
    const uniqueCategories = React.useMemo(() => {
        const categories = new Set<string>();
        filteredTemplates.forEach(template => {
            if (template.category) {
                categories.add(template.category);
            }
        });
        return Array.from(categories).sort();
    }, [filteredTemplates]);

    // メッセージタイプに応じて列定義を選択
    const columns = React.useMemo(() => {
        return usage === 'broadcast'
            ? broadcastColumns(handleDelete, uniqueCategories)
            : dmColumns(handleDelete, uniqueCategories);
    }, [usage, handleDelete, uniqueCategories]);

    return (
        <Spin spinning={loading}>
            <Table<MessageTemplate>
                onRow={onClickEdit}
                columns={columns}
                dataSource={filteredTemplates}
            />
        </Spin>
    );
};